// Consolidated country utility for Bonjour Residences OS.
// Single source of truth for country normalization, flag generation, and display formatting.

// ── Name lookup (uppercase key -> canonical English name) ─────────────
// Covers ISO-2, ISO-3 (common ones), full names, and common aliases.
export const COUNTRY_NAMES = {
  "UK": "United Kingdom",
  "GB": "United Kingdom",
  "UNITED KINGDOM": "United Kingdom",
  "GREAT BRITAIN": "United Kingdom",
  "ENGLAND": "United Kingdom",
  "FR": "France",
  "FRANCE": "France",
  "DE": "Germany",
  "GERMANY": "Germany",
  "NL": "Netherlands",
  "NETHERLANDS": "Netherlands",
  "THE NETHERLANDS": "Netherlands",
  "HOLLAND": "Netherlands",
  "DK": "Denmark",
  "DENMARK": "Denmark",
  "IE": "Ireland",
  "IRELAND": "Ireland",
  "ES": "Spain",
  "SPAIN": "Spain",
  "IT": "Italy",
  "ITALY": "Italy",
  "BE": "Belgium",
  "BELGIUM": "Belgium",
  "AT": "Austria",
  "AUSTRIA": "Austria",
  "CH": "Switzerland",
  "SWITZERLAND": "Switzerland",
  "PT": "Portugal",
  "PORTUGAL": "Portugal",
  "SE": "Sweden",
  "SWEDEN": "Sweden",
  "NO": "Norway",
  "NORWAY": "Norway",
  "FI": "Finland",
  "FINLAND": "Finland",
  "PL": "Poland",
  "POLAND": "Poland",
  "CZ": "Czech Republic",
  "CZECH REPUBLIC": "Czech Republic",
  "CZECHIA": "Czech Republic",
  "HU": "Hungary",
  "HUNGARY": "Hungary",
  "GR": "Greece",
  "GREECE": "Greece",
  "CY": "Cyprus",
  "CYPRUS": "Cyprus",
  "SK": "Slovakia",
  "SLOVAKIA": "Slovakia",
  "TR": "Turkey",
  "TURKEY": "Turkey",
  "TURKIYE": "Turkey",
  "\u0054\u00dcRKIYE": "Turkey",
  "HR": "Croatia",
  "CROATIA": "Croatia",
  "RO": "Romania",
  "ROMANIA": "Romania",
  "BG": "Bulgaria",
  "BULGARIA": "Bulgaria",
  "LT": "Lithuania",
  "LITHUANIA": "Lithuania",
  "LV": "Latvia",
  "LATVIA": "Latvia",
  "EE": "Estonia",
  "ESTONIA": "Estonia",
  "SI": "Slovenia",
  "SLOVENIA": "Slovenia",
  "MT": "Malta",
  "MALTA": "Malta",
  "LU": "Luxembourg",
  "LUXEMBOURG": "Luxembourg",
  "US": "United States",
  "USA": "United States",
  "UNITED STATES": "United States",
  "UNITED STATES OF AMERICA": "United States",
  "AE": "United Arab Emirates",
  "UAE": "United Arab Emirates",
  "UNITED ARAB EMIRATES": "United Arab Emirates",
  "SG": "Singapore",
  "SINGAPORE": "Singapore",
  "CA": "Canada",
  "CANADA": "Canada",
  "AU": "Australia",
  "AUSTRALIA": "Australia",
  "NZ": "New Zealand",
  "NEW ZEALAND": "New Zealand",
  "JP": "Japan",
  "JAPAN": "Japan",
  "CN": "China",
  "CHINA": "China",
};

// ── Canonical name -> ISO-2 reverse lookup ────────────────────────────
const NAME_TO_ISO2 = {
  "United Kingdom": "GB",
  "France": "FR",
  "Germany": "DE",
  "Netherlands": "NL",
  "Denmark": "DK",
  "Ireland": "IE",
  "Spain": "ES",
  "Italy": "IT",
  "Belgium": "BE",
  "Austria": "AT",
  "Switzerland": "CH",
  "Portugal": "PT",
  "Sweden": "SE",
  "Norway": "NO",
  "Finland": "FI",
  "Poland": "PL",
  "Czech Republic": "CZ",
  "Hungary": "HU",
  "Greece": "GR",
  "Cyprus": "CY",
  "Slovakia": "SK",
  "Turkey": "TR",
  "Croatia": "HR",
  "Romania": "RO",
  "Bulgaria": "BG",
  "Lithuania": "LT",
  "Latvia": "LV",
  "Estonia": "EE",
  "Slovenia": "SI",
  "Malta": "MT",
  "Luxembourg": "LU",
  "United States": "US",
  "United Arab Emirates": "AE",
  "Singapore": "SG",
  "Canada": "CA",
  "Australia": "AU",
  "New Zealand": "NZ",
  "Japan": "JP",
  "China": "CN",
};

// ── ISO-2 codes set (for quick validation) ────────────────────────────
const ISO2_CODES = new Set(Object.values(NAME_TO_ISO2));

// ── Flag computation from ISO-2 ───────────────────────────────────────
function flagFromIso2(iso2) {
  if (!iso2 || iso2.length !== 2) return "";
  const a = iso2.charCodeAt(0) - 65;
  const b = iso2.charCodeAt(1) - 65;
  if (a < 0 || a > 25 || b < 0 || b > 25) return "";
  return String.fromCodePoint(0x1F1E6 + a, 0x1F1E6 + b);
}

// ── Public API ────────────────────────────────────────────────────────

/**
 * Normalize any country input to canonical English full name.
 * Accepts ISO-2 codes, full names, lowercase, mixed case, common aliases.
 * Returns "Unknown" for null/undefined/empty. Returns input unchanged if unrecognized.
 */
export function normalizeCountry(input) {
  if (!input) return "Unknown";
  const key = String(input).trim().toUpperCase();
  return COUNTRY_NAMES[key] || String(input).trim();
}

/**
 * Convert any country input to ISO-2 uppercase code.
 * Returns null for unrecognized input.
 */
export function toIso2(input) {
  if (!input) return null;
  const key = String(input).trim().toUpperCase();
  // Direct ISO-2 code?
  if (key.length === 2 && ISO2_CODES.has(key)) return key;
  // "UK" is a common alias for "GB"
  if (key === "UK") return "GB";
  // Resolve via name lookup
  const name = COUNTRY_NAMES[key];
  if (!name) return null;
  return NAME_TO_ISO2[name] || null;
}

/**
 * Return flag emoji for any country input. Computes from ISO-2 via
 * regional indicator symbols. Returns "" for unrecognized input.
 */
export function countryFlag(input) {
  const iso2 = toIso2(input);
  if (!iso2) return "";
  return flagFromIso2(iso2);
}

/**
 * Format any country input as "flag name" (e.g. "\ud83c\udde9\ud83c\uddf0 Denmark").
 * Returns the original input unchanged if not recognized (never "undefined" or empty).
 * Returns "Unknown" for null/undefined/empty.
 */
export function formatCountry(input) {
  if (!input) return "Unknown";
  const name = normalizeCountry(input);
  const flag = countryFlag(input);
  if (!flag) return name;
  return `${flag} ${name}`;
}

// ── Dropdown option helpers ───────────────────────────────────────────

// Deduplicate COUNTRY_NAMES to unique canonical names, then build options.
// UK and US are pinned to the top (most-used), remainder sorted alphabetically.
const _PINNED = ["United Kingdom", "United States"];

function _buildOptions(valueFn) {
  const names = [...new Set(Object.values(COUNTRY_NAMES))];
  const pinned = _PINNED.filter((n) => names.includes(n));
  const rest = names.filter((n) => !_PINNED.includes(n)).sort((a, b) => a.localeCompare(b));
  return [...pinned, ...rest].map((name) => {
    const iso2 = NAME_TO_ISO2[name];
    const flag = iso2 ? flagFromIso2(iso2) : "";
    return {
      value: valueFn(name, iso2),
      label: flag ? `${flag} ${name}` : name,
    };
  });
}

/**
 * Dropdown options with full-name values.
 * Returns [{ value: "United Kingdom", label: "🇬🇧 United Kingdom" }, ...]
 * UK and US pinned first, remainder alphabetical.
 */
export function getCountryOptions() {
  return _buildOptions((name) => name);
}

/**
 * Dropdown options with ISO-2 values.
 * Returns [{ value: "GB", label: "🇬🇧 United Kingdom" }, ...]
 * For sites where the backend expects ISO-2 (e.g. Discover Providers).
 */
export function getCountryOptionsIso2() {
  return _buildOptions((name, iso2) => iso2 || name);
}

// ── Aliases for backward compatibility ────────────────────────────────
export const getCountryName = normalizeCountry;
